import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getVenues } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Building2 } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function VenuesPage() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('All');

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      const response = await getVenues();
      setVenues(response.data);
    } catch (error) {
      console.error('Failed to load venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVenues = venues.filter((v) => {
    if (cityFilter !== 'All' && v.city !== cityFilter) return false;
    return true;
  });

  const availableCities = ['All', ...new Set(venues.map(v => v.city).filter(Boolean))];

  if (loading) {
    return (
      <div className="content-container">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-card animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="content-container">
      {/* Hero Header */}
      <div className="mb-8">
        <h1 className="page-title gold-accent">Venues</h1>
        <p className="text-muted-foreground mt-2">
          {filteredVenues.length} venue{filteredVenues.length !== 1 ? 's' : ''} registered
        </p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-card rounded-lg border border-border">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-2">
            City
          </label>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="h-11 bg-secondary border-border" data-testid="city-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableCities.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results */}
      {filteredVenues.length === 0 ? (
        <Card className="p-8 text-center bg-card border-border">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No venues registered yet</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredVenues.map((venue) => (
            <Link key={venue.id} to={`/venues/${venue.id}`} data-testid={`venue-card-${venue.id}`}>
              <Card className="card-base profile-card transition-transform hover:-translate-y-0.5">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold truncate text-primary">{venue.venueName}</h3>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <MapPin className="h-4 w-4" />
                        <span>
                          {venue.city}, {venue.country}
                        </span>
                      </div>

                      {venue.contactPersonName && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          Contact: {venue.contactPersonName}
                        </div>
                      )}

                      {venue.shortBio && (
                        <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{venue.shortBio}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
